import {
    addDays,
    endOfMonth,
    formatISO,
    getDaysInMonth,
    startOfMonth,
    sub,
    subDays,
    subMonths,
} from "date-fns";
import { Period, period } from "src/modules/shared/components/PeriodSelector";

export const API_DATE_FORMAT = "yyyy-MM-dd";

/**
    t("shared.days.monday")
    t("shared.days.tuesday")
    t("shared.days.wednesday")
    t("shared.days.thursday")
    t("shared.days.friday")
    t("shared.days.saturday")
    t("shared.days.sunday")
 */

export const workDays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
export const weekendDays = ["saturday", "sunday"];

export const weekdays = [...workDays, ...weekendDays];

export const getFormatedDateForChart = (currentDate: Date) => {
    const formattedDate = `${currentDate.getDate().toString().padStart(2, "0")}.${
        currentDate.getMonth() + 1
    }.${currentDate.getFullYear().toString().padStart(2, "0")}`;

    return formattedDate;
};

export function getNumberOfDaysInCurrentMonth() {
    const today = new Date();
    const numberOfDays = getDaysInMonth(today);
    return numberOfDays;
}

export function getTodaysHours() {
    const hours = [];
    for (let i = 0; i <= 24; i = i + 2) {
        if (i === 24) {
            hours.push(`00:00`);
        } else {
            hours.push(`${i < 10 ? "0" : ""}${i}:00`);
        }
    }
    return hours;
}

export function getLastXHours(x: number) {
    const now = new Date();
    const hours = [];

    for (let i = 0; i < x; i++) {
        const pastHour = new Date(now.getTime() - i * 3600000);
        const hourString = `${pastHour.getHours() < 10 ? "0" : ""}${pastHour.getHours()}:00`;
        hours.unshift(getFormatedDateForChart(pastHour) + " - " + hourString);
    }

    return hours;
}

export function getHoursWithIntervalInPastDays(
    daysBack: number,
    intervalHours: number,
    hideModulusDays?: number
) {
    const now = new Date();
    const hours = [];

    for (let i = 0; i < daysBack; i++) {
        const pastDay = new Date(now.getTime() - i * 24 * 3600000);
        for (let j = 24; j >= 0; j -= intervalHours) {
            if (j < 24) {
                const timestamp = new Date(pastDay);
                timestamp.setHours(j, 0, 0, 0);

                const hourString =
                    intervalHours < 24
                        ? `${timestamp.getHours() < 10 ? "0" : ""}${timestamp.getHours()}:00`
                        : "";

                hours.unshift(
                    getFormatedDateForChart(pastDay) + (hourString ? " - " + hourString : "")
                );
            }
        }
    }

    if (hideModulusDays) {
        return hours.map((d, i) => {
            if (i % hideModulusDays === 0) {
                return d;
            }
            return "";
        });
    }
    return hours;
}

export function getNumberOfDaysInLastMonth() {
    const today = new Date();
    const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfLastMonth = sub(firstDayOfCurrentMonth, { days: 1 });
    const numberOfDays = getDaysInMonth(lastDayOfLastMonth);
    return numberOfDays;
}

export function getDaysInPreviousMonth(hideModulusDays?: number) {
    const today = new Date();
    const lastMonth = new Date(today);
    lastMonth.setMonth(today.getMonth() - 1);

    const days = [];
    const startDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
    const endDate = new Date(today.getFullYear(), today.getMonth(), 0);

    const currentDate = startDate;

    while (currentDate <= endDate) {
        days.push(getFormatedDateForChart(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    if (hideModulusDays) {
        return days.map((d, i) => {
            if (i % hideModulusDays === 0) {
                return d;
            }
            return "";
        });
    }
    return days;
}

export function getDaysInCurrentMonth(hideModulusDays?: number) {
    const today = new Date();
    const thisMonth = new Date(today);
    thisMonth.setMonth(today.getMonth());

    const days = [];
    const startDate = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1);
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const currentDate = startDate;

    while (currentDate <= endDate) {
        days.push(getFormatedDateForChart(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    if (hideModulusDays) {
        return days.map((d, i) => {
            if (i % hideModulusDays === 0) {
                return d;
            }
            return "";
        });
    }

    return days;
}

export function isDateBetween(myDate: Date, from: Date, to: Date) {
    return myDate >= from && myDate <= to;
}

export const resolvePeriodFrom = (period: Period) => {
    switch (period) {
        case "today":
            return formatISO(new Date(), { representation: "date" });
        case "yesterday":
            return formatISO(subDays(new Date(), 1), { representation: "date" });
        case "7days":
            return formatISO(subDays(new Date(), 7), { representation: "date" });
        case "14days":
            return formatISO(subDays(new Date(), 14), { representation: "date" });
        case "30days":
            return formatISO(subDays(new Date(), 30), { representation: "date" });
        case "last_month":
            return formatISO(subMonths(startOfMonth(new Date()), 1), { representation: "date" });
        case "this_month":
            return formatISO(startOfMonth(new Date()), { representation: "date" });
        default:
            return formatISO(startOfMonth(new Date()), { representation: "date" });
    }
};

export const resolvePeriodTo = (period: Period) => {
    switch (period) {
        case "today":
            return formatISO(addDays(new Date(), 1), { representation: "date" });
        case "yesterday":
            return formatISO(new Date(), { representation: "date" });
        case "last_month":
            return formatISO(startOfMonth(new Date()), { representation: "date" });
        case "this_month":
            return formatISO(endOfMonth(new Date()), { representation: "date" });
        default:
            return formatISO(new Date(), { representation: "date" });
    }
};

export const getDateFromToFromPeriod = (period: Period) => {
    return { from: resolvePeriodFrom(period), to: resolvePeriodTo(period) };
};

export const datesMatchAnyPeriod = (from: string, to: string): Period => {
    let selectedPeriod: Period = "custom";
    if (from || to) {
        period
            .filter((p) => p !== "custom")
            .forEach((p) => {
                const fromPeriod = resolvePeriodFrom(p as Period);
                const toPeriod = resolvePeriodTo(p as Period);

                if (fromPeriod === from && toPeriod === to) {
                    selectedPeriod = p as Period;
                }
            });
    }

    return selectedPeriod;
};
export const getFromToForPeriod = (selectedPeriod: Period, from: string, to: string) => {
    return selectedPeriod !== "custom"
        ? getDateFromToFromPeriod(selectedPeriod)
        : { from: (from as string) || "", to: (to as string) || "" };
};
